import React, { useRef, useEffect, useState } from "react";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import CodeIcon from "@mui/icons-material/Code";

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder,
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isHtmlMode, setIsHtmlMode] = useState(false);

    // Sync contenteditable content when value updates externally
    useEffect(() => {
        if (editorRef.current && !isHtmlMode) {
            if (editorRef.current.innerHTML !== value) {
                editorRef.current.innerHTML = value || "";
            }
        }
    }, [value, isHtmlMode]);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const execCmd = (command: string, arg = "") => {
        if (isHtmlMode) return;
        document.execCommand(command, false, arg);
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const handleLink = () => {
        if (isHtmlMode) return;
        const url = prompt("Enter link URL:");
        if (url) {
            execCmd("createLink", url);
        }
    };

    return (
        <div style={{
            border: "1.5px solid var(--border, #e2e8f0)",
            borderRadius: "8px",
            overflow: "hidden",
            background: "var(--surface, #ffffff)",
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Toolbar */}
            <div style={{
                display: "flex",
                gap: "4px",
                padding: "6px 8px",
                borderBottom: "1.5px solid var(--border, #e2e8f0)",
                backgroundColor: "var(--surface-2, #f8fafc)",
                alignItems: "center"
            }}>
                <button
                    type="button"
                    onClick={() => execCmd("bold")}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Bold"
                >
                    <FormatBoldIcon fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={() => execCmd("italic")}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Italic"
                >
                    <FormatItalicIcon fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={() => execCmd("underline")}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Underline"
                >
                    <FormatUnderlinedIcon fontSize="small" />
                </button>
                
                <div style={{ width: "1px", height: "18px", backgroundColor: "var(--border, #e2e8f0)", margin: "0 4px" }} />
                
                <button
                    type="button"
                    onClick={() => execCmd("insertUnorderedList")}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Bullet List"
                >
                    <FormatListBulletedIcon fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={() => execCmd("insertOrderedList")}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Numbered List"
                >
                    <FormatListNumberedIcon fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={handleLink}
                    disabled={isHtmlMode}
                    style={{ padding: "4px", cursor: "pointer", background: "none", border: "none", color: "var(--text-2, #475569)" }}
                    title="Insert Link"
                >
                    <InsertLinkIcon fontSize="small" />
                </button>

                <div style={{ width: "1px", height: "18px", backgroundColor: "var(--border, #e2e8f0)", margin: "0 4px" }} />

                <button
                    type="button"
                    onClick={() => setIsHtmlMode(!isHtmlMode)}
                    style={{
                        padding: "4px 8px",
                        cursor: "pointer",
                        background: isHtmlMode ? "var(--border, #e2e8f0)" : "none",
                        border: "1px solid var(--border, #e2e8f0)",
                        borderRadius: "4px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "var(--text-2, #475569)"
                    }}
                    title="Toggle HTML Source"
                >
                    <CodeIcon fontSize="inherit" />
                    <span>{isHtmlMode ? "Visual" : "HTML"}</span>
                </button>
            </div>

            {/* Editor Area */}
            {isHtmlMode ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        minHeight: "200px",
                        padding: "12px",
                        outline: "none",
                        border: "none",
                        backgroundColor: "var(--surface-2, #f8fafc)",
                        color: "var(--text, #1e293b)",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        resize: "vertical"
                    }}
                    placeholder="HTML Code"
                />
            ) : (
                <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleInput}
                    style={{
                        minHeight: "200px",
                        padding: "12px",
                        outline: "none",
                        backgroundColor: "var(--surface, #ffffff)",
                        color: "var(--text, #1e293b)",
                        overflowY: "auto",
                        fontSize: "14px"
                    }}
                    placeholder={placeholder}
                />
            )}
        </div>
    );
};
